import type { ExerciseTemplate, Trap } from '@yuan-math/exercises';
import { fraction, gcd, roundTo, toLatex, toText } from '@yuan-math/math';
import {
  choiceExercise,
  decimalTolerance,
  fractionExercise,
  fractionTrap,
  numericExercise,
  options,
  template,
  trap,
} from './authoring.ts';

/**
 * Similarity, polygons, circle theorems, arcs and sectors, coordinate
 * geometry and right-triangle trigonometry in both directions.
 */
export const GEOMETRY_HS_TEMPLATES: ExerciseTemplate[] = [
  template({
    id: 'similarity-missing-side',
    conceptId: 'similarity',
    description: 'Use a scale factor to find a missing side in similar triangles.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const factor = rng.int(2, difficulty === 4 ? 6 : 4);
      const a = rng.int(2, 9);
      const b = rng.int(2, 9);
      const bigA = a * factor;
      const answer = b * factor;

      return numericExercise({
        prompt: `Two triangles are similar. A side of ${a} cm on the smaller one corresponds to ${bigA} cm on the larger. What does a side of ${b} cm correspond to?`,
        unit: 'cm',
        value: answer,
        summary: `The scale factor is ${bigA} ÷ ${a} = ${factor}, so ${b} × ${factor} = ${answer} cm.`,
        hint: 'Find what the first pair of sides was multiplied by, then use the same factor.',
        steps: [
          { label: 'Find the scale factor.', latex: `${bigA} \\div ${a} = ${factor}` },
          { label: 'Apply it to the other side.', latex: `${b} \\times ${factor} = ${answer}` },
        ],
        traps: [
          ...(roundTo(b / factor, 4) === answer ? [] : [trap(roundTo(b / factor, 4), 'scale-factor-inverted')]),
          ...(b + (bigA - a) === answer ? [] : [trap(b + (bigA - a), 'proportion-additive')]),
        ],
      });
    },
  }),

  template({
    id: 'similarity-area-scale',
    conceptId: 'similarity',
    description: 'Scale an area, where the factor is squared.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const factor = rng.int(2, 5);
      const area = rng.int(2, 20) * 3;
      const answer = area * factor * factor;

      return numericExercise({
        prompt: `A shape is enlarged by a scale factor of ${factor}. Its original area was ${area} cm². What is the area of the enlargement?`,
        unit: 'square cm',
        value: answer,
        summary: `Every length is multiplied by ${factor}, and area involves two lengths, so the area is multiplied by ${factor}² = ${factor * factor}: ${answer} cm².`,
        hint: 'An enlargement stretches the shape in both directions at once. What does that do to the area?',
        steps: [
          { label: 'Area scales by the square of the length factor.', latex: `${factor}^2 = ${factor * factor}` },
          { label: 'Multiply.', latex: `${area} \\times ${factor * factor} = ${answer}` },
        ],
        traps: [trap(area * factor, 'area-scaled-linearly')],
      });
    },
  }),

  template({
    id: 'side-splitter-missing-piece',
    conceptId: 'triangle-proportionality',
    description: 'Find a missing piece with the side-splitter theorem.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      // AD : DB = AE : EC = p : q, scaled independently on each side.
      const p = rng.int(2, 5);
      const q = rng.pick([2, 3, 4, 5].filter((value) => value !== p));
      const scaleOne = rng.int(2, 6);
      const scaleTwo = rng.int(2, 6);
      const ad = p * scaleOne;
      const db = q * scaleOne;
      const ae = p * scaleTwo;
      const ec = q * scaleTwo;

      return numericExercise({
        prompt: `In triangle ABC, D lies on AB and E lies on AC, with DE parallel to BC. AD = ${ad} cm, DB = ${db} cm and AE = ${ae} cm. How long is EC?`,
        unit: 'cm',
        value: ec,
        summary: `DE is parallel to BC, so AD/DB = AE/EC. That gives ${ad}/${db} = ${ae}/EC, and EC = ${db} × ${ae} ÷ ${ad} = ${ec} cm.`,
        hint: 'Pair the two pieces nearest A together, and the two pieces nearest the base together.',
        steps: [
          { label: 'The parallel line cuts both sides in the same ratio.', latex: `\\frac{AD}{DB} = \\frac{AE}{EC}` },
          { label: 'Substitute.', latex: `\\frac{${ad}}{${db}} = \\frac{${ae}}{EC}` },
          { label: 'Cross-multiply and divide.', latex: `EC = \\frac{${db} \\times ${ae}}{${ad}} = ${ec}` },
        ],
        traps: [
          trap(roundTo((db * ae) / (ad + db), 2), 'side-splitter-whole-for-part'),
          trap(roundTo((ad * ae) / db, 2), 'scale-factor-inverted'),
        ],
      });
    },
  }),

  template({
    id: 'triangle-midsegment',
    conceptId: 'triangle-proportionality',
    description: 'Use the midsegment theorem in either direction.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const half = rng.int(4, 24);
      const whole = half * 2;
      const wantMidsegment = rng.bool();

      return numericExercise({
        prompt: wantMidsegment
          ? `In a triangle, the third side is ${whole} cm long. A segment joins the midpoints of the other two sides. How long is it?`
          : `In a triangle, the segment joining the midpoints of two sides is ${half} cm long. How long is the third side?`,
        unit: 'cm',
        value: wantMidsegment ? half : whole,
        summary: `The segment joining two midpoints is parallel to the third side and exactly half its length, so the answer is ${wantMidsegment ? half : whole} cm.`,
        hint: 'The small triangle is the whole one at a scale factor of one half.',
        steps: [
          {
            label: wantMidsegment ? 'Halve the third side.' : 'Double the midsegment.',
            latex: wantMidsegment ? `${whole} \\div 2 = ${half}` : `${half} \\times 2 = ${whole}`,
          },
        ],
        traps: [trap(wantMidsegment ? whole : half, 'midsegment-not-halved')],
      });
    },
  }),

  template({
    id: 'angle-bisector-theorem',
    conceptId: 'triangle-proportionality',
    description: 'Split the opposite side with the angle bisector theorem.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const near = rng.int(3, 9);
      const far = rng.pick([3, 4, 5, 6, 7, 8, 9, 10, 11, 12].filter((value) => value !== near));
      const scale = rng.int(2, 5);
      const bd = near * scale;
      const dc = far * scale;

      return numericExercise({
        prompt: `In triangle ABC the bisector of angle A meets BC at D. AB = ${near} cm, AC = ${far} cm and BD = ${bd} cm. How long is DC?`,
        unit: 'cm',
        value: dc,
        summary: `The bisector splits BC in the ratio of the two sides forming the angle: BD/DC = AB/AC, so DC = ${bd} × ${far} ÷ ${near} = ${dc} cm.`,
        hint: 'BD sits next to AB, and DC sits next to AC. Keep those pairs together.',
        steps: [
          { label: 'The bisector theorem.', latex: `\\frac{BD}{DC} = \\frac{AB}{AC}` },
          { label: 'Substitute.', latex: `\\frac{${bd}}{DC} = \\frac{${near}}{${far}}` },
          { label: 'Rearrange.', latex: `DC = \\frac{${bd} \\times ${far}}{${near}} = ${dc}` },
        ],
        traps: [trap(roundTo((bd * near) / far, 2), 'scale-factor-inverted')],
      });
    },
  }),

  template({
    id: 'side-splitter-parallel-test',
    conceptId: 'triangle-proportionality',
    description: 'Use the converse of the side-splitter theorem to test for a parallel line.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const p = rng.int(2, 5);
      const q = rng.pick([2, 3, 4, 5].filter((value) => value !== p));
      const scaleOne = rng.int(2, 4);
      const scaleTwo = rng.int(2, 4);
      const parallel = rng.bool();
      const ad = p * scaleOne;
      const db = q * scaleOne;
      const ae = p * scaleTwo;
      const ec = parallel ? q * scaleTwo : q * scaleTwo + rng.int(1, 3);

      const { choices, correctId } = options(
        [
          parallel
            ? { label: 'Yes — the two sides are cut in the same ratio', correct: true }
            : { label: 'No — the two ratios are different', correct: true },
          { label: parallel ? 'No — the four lengths are not equal in pairs' : 'Yes — both cuts are nearer A than the base', misconceptionId: 'side-splitter-whole-for-part' },
          { label: 'Yes — comparing AD with EC gives a whole number', misconceptionId: 'side-splitter-whole-for-part' },
          { label: 'It cannot be decided without measuring the angles', misconceptionId: 'measurement-taken-as-proof' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `In triangle ABC, D lies on AB with AD = ${ad} and DB = ${db}, and E lies on AC with AE = ${ae} and EC = ${ec}. Is DE parallel to BC?`,
        choices,
        correctId,
        summary: parallel
          ? `AD/DB = ${ad}/${db} and AE/EC = ${ae}/${ec}, and those are the same ratio — so by the converse of the side-splitter theorem DE is parallel to BC.`
          : `AD/DB = ${ad}/${db} but AE/EC = ${ae}/${ec}, and those are different ratios, so DE cannot be parallel to BC.`,
        hint: 'Work out both ratios as fractions and compare them.',
      });
    },
  }),

  template({
    id: 'quad-named-from-diagonals',
    conceptId: 'quadrilateral-properties',
    description: 'Name a quadrilateral from what its diagonals do.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      /*
       * The near miss is chosen per shape, because the two guarantees that get
       * swapped are not the same pair every time: a rectangle is mistaken for a
       * rhombus over *perpendicular*, a kite for a rhombus over *bisecting*.
       */
      const item = rng.pick([
        {
          name: 'Rectangle',
          clue: 'bisect each other and are equal in length, but do not meet at right angles',
          near: { label: 'Rhombus', misconceptionId: 'rhombus-diagonals-taken-equal' },
        },
        {
          name: 'Rhombus',
          clue: 'bisect each other at right angles, but are not equal in length',
          near: { label: 'Rectangle', misconceptionId: 'rhombus-diagonals-taken-equal' },
        },
        {
          name: 'Square',
          clue: 'bisect each other, are equal in length, and meet at right angles',
          near: { label: 'Rhombus', misconceptionId: 'rhombus-diagonals-taken-equal' },
        },
        {
          name: 'Kite',
          clue: 'meet at right angles, but only one of them bisects the other',
          near: { label: 'Rhombus', misconceptionId: 'kite-taken-as-parallelogram' },
        },
      ]);

      const { choices, correctId } = options(
        [
          { label: item.name, correct: true },
          item.near,
          { label: 'Any parallelogram', misconceptionId: 'parallelogram-diagonals-taken-equal' },
          { label: 'Trapezium', misconceptionId: 'kite-taken-as-parallelogram' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `The diagonals of a quadrilateral ${item.clue}. What is the shape?`,
        choices,
        correctId,
        summary: `Equal diagonals mean a rectangle, perpendicular diagonals mean a rhombus, and both together mean a square. Diagonals meeting at right angles where only one bisects the other is a kite. This one is a ${item.name.toLowerCase()}.`,
        hint: 'Take the clues one at a time and cross a shape off with each.',
      });
    },
  }),

  template({
    id: 'parallelogram-consecutive-angle',
    conceptId: 'quadrilateral-properties',
    description: 'Use the opposite and consecutive angles of a parallelogram.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const given = rng.int(35, 145);
      const wantOpposite = rng.bool();
      const answer = wantOpposite ? given : 180 - given;

      return numericExercise({
        prompt: wantOpposite
          ? `One angle of a parallelogram is ${given}°. What is the angle opposite it?`
          : `One angle of a parallelogram is ${given}°. What is the angle next to it?`,
        unit: 'degrees',
        value: answer,
        summary: wantOpposite
          ? `Opposite angles of a parallelogram are equal, so the answer is ${given}°.`
          : `Consecutive angles of a parallelogram lie between two parallel sides, so they are co-interior and add to 180°: 180 − ${given} = ${answer}°.`,
        hint: 'Opposite angles match; the two at either end of one side add to 180°.',
        steps: [
          {
            label: wantOpposite ? 'Opposite angles are equal.' : 'Consecutive angles are co-interior.',
            latex: wantOpposite ? `${given}` : `180 - ${given} = ${answer}`,
          },
        ],
        /*
         * Only the consecutive case carries a trap. Answering `given` there is
         * exactly co-interior angles taken as equal; the same slip in reverse —
         * subtracting where the angles simply match — is not that misconception
         * and would be given the wrong name.
         */
        traps: wantOpposite ? [] : [trap(given, 'co-interior-taken-equal')],
      });
    },
  }),

  template({
    id: 'trapezium-midsegment',
    conceptId: 'quadrilateral-properties',
    description: 'Find the midsegment of a trapezium, or a missing parallel side.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const shorter = rng.int(4, 20);
      const longer = shorter + 2 * rng.int(1, 10);
      const middle = (shorter + longer) / 2;
      const wantMiddle = rng.bool();

      return numericExercise({
        prompt: wantMiddle
          ? `A trapezium has parallel sides of ${shorter} cm and ${longer} cm. How long is the segment joining the midpoints of its other two sides?`
          : `A trapezium has one parallel side of ${shorter} cm, and the segment joining the midpoints of its other two sides is ${middle} cm. How long is the second parallel side?`,
        unit: 'cm',
        value: wantMiddle ? middle : longer,
        summary: wantMiddle
          ? `The midsegment is the average of the two parallel sides: (${shorter} + ${longer}) ÷ 2 = ${middle} cm.`
          : `The midsegment is the average, so the two parallel sides total 2 × ${middle} = ${2 * middle}, leaving ${2 * middle} − ${shorter} = ${longer} cm.`,
        hint: 'The midsegment sits halfway between the two parallel sides, so it is their average.',
        steps: [
          {
            label: wantMiddle ? 'Average the two parallel sides.' : 'Double the midsegment, then subtract the known side.',
            latex: wantMiddle
              ? `\\frac{${shorter} + ${longer}}{2} = ${middle}`
              : `2 \\times ${middle} - ${shorter} = ${longer}`,
          },
        ],
        traps: [trap(wantMiddle ? shorter + longer : middle - shorter, 'trapezoid-midsegment-not-averaged')],
      });
    },
  }),

  template({
    id: 'kite-missing-angle',
    conceptId: 'quadrilateral-properties',
    description: 'Use the one pair of equal angles in a kite.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const apex = rng.int(40, 110);
      const tail = rng.int(40, 110);
      // Keep the two equal angles a whole number of degrees.
      const usableTail = (apex + tail) % 2 === 0 ? tail : tail + 1;
      const equal = (360 - apex - usableTail) / 2;

      return numericExercise({
        prompt: `A kite has angles of ${apex}° and ${usableTail}° at the two ends of its axis of symmetry. The other two angles are equal. How big is each of them?`,
        unit: 'degrees',
        value: equal,
        summary: `The four angles total 360°, and the two off the axis are equal: (360 − ${apex} − ${usableTail}) ÷ 2 = ${equal}°.`,
        hint: 'A kite has one mirror line, and the two angles it does not pass through are the equal pair.',
        steps: [
          { label: 'The angles of any quadrilateral total 360°.', latex: `360 - ${apex} - ${usableTail} = ${360 - apex - usableTail}` },
          { label: 'Share what is left between the two equal angles.', latex: `${360 - apex - usableTail} \\div 2 = ${equal}` },
        ],
        traps: [trap(360 - apex - usableTail, 'kite-taken-as-parallelogram')],
      });
    },
  }),

  template({
    id: 'quad-property-guaranteed',
    conceptId: 'quadrilateral-properties',
    description: 'Decide which property a named quadrilateral guarantees.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const item = rng.pick([
        { shape: 'parallelogram', answer: 'Its diagonals bisect each other', wrong: 'Its diagonals are equal in length', missed: 'parallelogram-diagonals-taken-equal' },
        { shape: 'rhombus', answer: 'Its diagonals meet at right angles', wrong: 'Its diagonals are equal in length', missed: 'rhombus-diagonals-taken-equal' },
        { shape: 'rectangle', answer: 'Its diagonals are equal in length', wrong: 'Its diagonals meet at right angles', missed: 'rhombus-diagonals-taken-equal' },
        { shape: 'kite', answer: 'Its diagonals meet at right angles', wrong: 'Its opposite sides are parallel', missed: 'kite-taken-as-parallelogram' },
      ]);

      const { choices, correctId } = options(
        [
          { label: item.answer, correct: true },
          { label: item.wrong, misconceptionId: item.missed },
          { label: 'All four of its sides are equal', misconceptionId: 'kite-taken-as-parallelogram' },
          { label: 'All four of its angles are right angles', misconceptionId: 'parallelogram-diagonals-taken-equal' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Which of these is true of every ${item.shape}?`,
        choices,
        correctId,
        summary: `${item.answer} — that one holds for every ${item.shape}. The others describe a different member of the family.`,
        hint: 'Try to draw one that breaks each option. If you can, that option is not guaranteed.',
      });
    },
  }),

  template({
    id: 'chord-from-perpendicular',
    conceptId: 'chords-secants',
    description: 'Find a chord from the radius and its distance from the centre.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const triple = rng.pick([
        { half: 3, distance: 4, radius: 5 },
        { half: 4, distance: 3, radius: 5 },
        { half: 8, distance: 6, radius: 10 },
        { half: 12, distance: 5, radius: 13 },
        { half: 5, distance: 12, radius: 13 },
        { half: 15, distance: 8, radius: 17 },
        { half: 24, distance: 7, radius: 25 },
      ]);
      const scale = rng.int(1, 3);
      const half = triple.half * scale;
      const distance = triple.distance * scale;
      const radius = triple.radius * scale;

      return numericExercise({
        prompt: `A circle has radius ${radius} cm. A chord is ${distance} cm from the centre. How long is the chord?`,
        unit: 'cm',
        figure: { kind: 'circle-diagram', elements: ['chord', 'radius'] },
        value: half * 2,
        summary: `The perpendicular from the centre bisects the chord, so half the chord, the distance and the radius form a right triangle: half = √(${radius}² − ${distance}²) = ${half}, and the whole chord is ${half * 2} cm.`,
        hint: 'The right triangle contains only half of the chord.',
        steps: [
          { label: 'Pythagoras on the right triangle.', latex: `h^2 = ${radius}^2 - ${distance}^2 = ${half * half}` },
          { label: 'That is half the chord.', latex: `h = ${half}` },
          { label: 'Double it.', latex: `${half} \\times 2 = ${half * 2}` },
        ],
        traps: [trap(half, 'chord-bisector-halved-twice')],
      });
    },
  }),

  template({
    id: 'intersecting-chords',
    conceptId: 'chords-secants',
    description: 'Apply the intersecting chords theorem.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      // a × b = c × d, built so every length is a whole number.
      const a = rng.int(2, 9);
      const b = rng.int(2, 9);
      const c = rng.pick([1, 2, 3, 4, 6, 9, 12, 18].filter((value) => (a * b) % value === 0 && value !== a && value !== b));
      const d = (a * b) / c;

      return numericExercise({
        prompt: `Two chords of a circle cross inside it. The first is cut into pieces of ${a} cm and ${b} cm. One piece of the second is ${c} cm. How long is the other piece?`,
        unit: 'cm',
        value: d,
        summary: `Where two chords cross, the products of their pieces are equal: ${a} × ${b} = ${c} × d, so d = ${a * b} ÷ ${c} = ${d} cm.`,
        hint: 'The two pieces of one chord multiply to the same value as the two pieces of the other.',
        steps: [
          { label: 'The intersecting chords theorem.', latex: `${a} \\times ${b} = ${c} \\times d` },
          { label: 'Divide.', latex: `d = \\frac{${a * b}}{${c}} = ${d}` },
        ],
        traps: a + b - c > 0 ? [trap(a + b - c, 'intersecting-chords-added')] : [],
      });
    },
  }),

  template({
    id: 'tangent-secant-length',
    conceptId: 'chords-secants',
    description: 'Find a tangent length from a secant through the same point.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const pair = rng.pick([
        { outside: 4, whole: 9, tangent: 6 },
        { outside: 2, whole: 8, tangent: 4 },
        { outside: 3, whole: 12, tangent: 6 },
        { outside: 4, whole: 16, tangent: 8 },
        { outside: 5, whole: 20, tangent: 10 },
        { outside: 2, whole: 18, tangent: 6 },
        { outside: 9, whole: 16, tangent: 12 },
      ]);
      const chord = pair.whole - pair.outside;

      return numericExercise({
        prompt: `From a point outside a circle, a tangent and a secant are drawn. The secant meets the circle first at ${pair.outside} cm from the point, and the chord inside the circle is ${chord} cm long. How long is the tangent?`,
        unit: 'cm',
        figure: { kind: 'circle-diagram', elements: ['tangent', 'chord'] },
        value: pair.tangent,
        summary: `The tangent squared equals the outside piece times the whole secant: t² = ${pair.outside} × (${pair.outside} + ${chord}) = ${pair.outside * pair.whole}, so t = ${pair.tangent} cm.`,
        hint: 'The second length is the whole secant, from the point right through to the far side — add the outside piece to the chord first.',
        steps: [
          { label: 'Build the whole secant.', latex: `${pair.outside} + ${chord} = ${pair.whole}` },
          { label: 'The tangent-secant relationship.', latex: `t^2 = ${pair.outside} \\times ${pair.whole} = ${pair.outside * pair.whole}` },
          { label: 'Take the root.', latex: `t = ${pair.tangent}` },
        ],
        traps: [
          trap(roundTo(Math.sqrt(pair.outside * chord), 2), 'secant-external-part-used-whole'),
          trap(pair.outside * pair.whole, 'tangent-length-not-squared'),
        ],
      });
    },
  }),

  template({
    id: 'two-secants-from-a-point',
    conceptId: 'chords-secants',
    description: 'Apply the secant form of the power of a point.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const outsideOne = rng.int(2, 6);
      const wholeOne = outsideOne * rng.int(2, 5);
      const power = outsideOne * wholeOne;
      const outsideTwo = rng.pick([1, 2, 3, 4, 5, 6, 8, 9, 12].filter((value) => power % value === 0 && value * value < power));
      const wholeTwo = power / outsideTwo;
      const chordOne = wholeOne - outsideOne;

      return numericExercise({
        prompt: `Two secants are drawn to a circle from the same outside point. The first meets the circle at ${outsideOne} cm and ${wholeOne} cm from the point. The second meets it first at ${outsideTwo} cm from the point. How far from the point is its second intersection?`,
        unit: 'cm',
        value: wholeTwo,
        summary: `For each secant, the outside piece times the whole secant is the same: ${outsideOne} × ${wholeOne} = ${power}, so the second whole secant is ${power} ÷ ${outsideTwo} = ${wholeTwo} cm.`,
        hint: 'Both lengths are measured from the outside point — the near intersection and the far one.',
        steps: [
          { label: 'The power of the point, from the first secant.', latex: `${outsideOne} \\times ${wholeOne} = ${power}` },
          { label: 'Divide by the outside piece of the second.', latex: `\\frac{${power}}{${outsideTwo}} = ${wholeTwo}` },
        ],
        traps: [trap(roundTo((outsideOne * chordOne) / outsideTwo, 2), 'secant-external-part-used-whole')],
      });
    },
  }),

  template({
    id: 'chord-distance-from-centre',
    conceptId: 'chords-secants',
    description: 'Find how far a chord is from the centre.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const triple = rng.pick([
        { half: 3, distance: 4, radius: 5 },
        { half: 8, distance: 6, radius: 10 },
        { half: 12, distance: 5, radius: 13 },
        { half: 5, distance: 12, radius: 13 },
        { half: 15, distance: 8, radius: 17 },
        { half: 24, distance: 7, radius: 25 },
      ]);
      const scale = rng.int(1, 3);
      const half = triple.half * scale;
      const distance = triple.distance * scale;
      const radius = triple.radius * scale;

      return numericExercise({
        prompt: `A chord of length ${half * 2} cm is drawn in a circle of radius ${radius} cm. How far is the chord from the centre?`,
        unit: 'cm',
        figure: { kind: 'circle-diagram', elements: ['chord', 'radius'] },
        value: distance,
        summary: `Halve the chord first: ${half * 2} ÷ 2 = ${half}. Then Pythagoras gives d = √(${radius}² − ${half}²) = ${distance} cm.`,
        hint: 'Halve the chord before using Pythagoras — the right triangle only contains half of it.',
        steps: [
          { label: 'Halve the chord.', latex: `${half * 2} \\div 2 = ${half}` },
          { label: 'Pythagoras.', latex: `d^2 = ${radius}^2 - ${half}^2 = ${distance * distance}` },
          { label: 'Take the root.', latex: `d = ${distance}` },
        ],
      });
    },
  }),

  template({
    id: 'polygon-interior-sum',
    conceptId: 'polygons',
    description: 'Find the interior angle sum of a polygon.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const sides = rng.int(3, difficulty === 2 ? 8 : 12);
      const answer = (sides - 2) * 180;

      return numericExercise({
        prompt: `What do the interior angles of a polygon with ${sides} sides add up to?`,
        unit: 'degrees',
        figure: { kind: 'polygon', sides, showCentre: true, label: `${sides} sides` },
        value: answer,
        summary: `It splits into ${sides} − 2 = ${sides - 2} triangles, each holding 180°: ${answer}°.`,
        hint: 'Draw diagonals from one corner. How many triangles does that make?',
        steps: [
          { label: 'Count the triangles.', latex: `${sides} - 2 = ${sides - 2}` },
          { label: 'Each holds 180°.', latex: `${sides - 2} \\times 180 = ${answer}` },
        ],
        // The exterior sum, which is 360° whatever the polygon.
        traps: [...(answer === 360 ? [] : [trap(360, 'polygon-angle-sum-wrong')])],
      });
    },
  }),

  template({
    id: 'polygon-exterior-angle',
    conceptId: 'polygons',
    description: 'Find one exterior angle of a regular polygon.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const sides = rng.pick([3, 4, 5, 6, 8, 9, 10, 12]);
      const answer = 360 / sides;
      const interior = 180 - answer;

      return numericExercise({
        prompt: `What is the size of one exterior angle of a regular polygon with ${sides} sides?`,
        unit: 'degrees',
        figure: { kind: 'polygon', sides, label: `regular, ${sides} sides` },
        value: answer,
        summary: `The exterior angles always total 360°, and here they are all equal: 360 ÷ ${sides} = ${answer}°.`,
        hint: 'Walk once around the outside of the shape. How far have you turned in total?',
        steps: [
          { label: 'The exterior angles add to 360°.', latex: `360` },
          { label: 'Share equally between the corners.', latex: `360 \\div ${sides} = ${answer}` },
        ],
        traps: [...(interior === answer ? [] : [trap(interior, 'polygon-angle-sum-wrong')])],
      });
    },
  }),

  template({
    id: 'polygon-interior-angle',
    conceptId: 'polygons',
    description: 'Find one interior angle of a regular polygon.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const sides = rng.pick([3, 4, 5, 6, 8, 9, 10, 12, 15, 18, 20]);
      const exterior = 360 / sides;
      const answer = 180 - exterior;

      return numericExercise({
        prompt: `What is the size of one interior angle of a regular polygon with ${sides} sides?`,
        unit: 'degrees',
        figure: { kind: 'polygon', sides: Math.min(sides, 12), label: `regular, ${sides} sides` },
        value: answer,
        summary: `Each exterior angle is 360 ÷ ${sides} = ${exterior}°, and an interior angle is what is left on the straight line: 180 − ${exterior} = ${answer}°.`,
        hint: 'Find the exterior angle first — it is much easier — then subtract from 180°.',
        steps: [
          { label: 'One exterior angle.', latex: `360 \\div ${sides} = ${exterior}` },
          { label: 'Interior and exterior make a straight line.', latex: `180 - ${exterior} = ${answer}` },
        ],
        traps: [
          ...(exterior === answer ? [] : [trap(exterior, 'polygon-angle-sum-wrong')]),
          ...((sides - 2) * 180 === answer ? [] : [trap((sides - 2) * 180, 'polygon-angle-sum-wrong')]),
        ],
      });
    },
  }),

  template({
    id: 'quadrilateral-properties',
    conceptId: 'polygons',
    description: 'Identify a quadrilateral from its properties.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const shape = rng.pick([
        { name: 'Parallelogram', clue: 'both pairs of opposite sides are parallel and equal, but no angle is a right angle' },
        { name: 'Rhombus', clue: 'all four sides are equal but the angles are not right angles' },
        { name: 'Rectangle', clue: 'all four angles are right angles but adjacent sides have different lengths' },
        { name: 'Trapezium', clue: 'exactly one pair of sides is parallel' },
      ]);
      const others = ['Parallelogram', 'Rhombus', 'Rectangle', 'Trapezium', 'Square'].filter((n) => n !== shape.name);
      const distractors = rng.shuffle(others).slice(0, 3);

      const { choices, correctId } = options(
        [
          { label: shape.name, correct: true },
          ...distractors.map((name) => ({ label: name, misconceptionId: 'angle-pair-misnamed' })),
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `A quadrilateral has this property: ${shape.clue}. What is it?`,
        choices,
        correctId,
        summary: `That describes a ${shape.name.toLowerCase()}.`,
        hint: 'Work through the properties one at a time and rule out what cannot fit.',
      });
    },
  }),

  template({
    id: 'special-triangle-30-60',
    conceptId: 'special-triangles',
    description: 'Use the 30-60-90 ratio.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const short = rng.int(2, 15);
      const hypotenuse = short * 2;
      const wantHypotenuse = rng.bool();

      return numericExercise({
        prompt: wantHypotenuse
          ? `In a 30-60-90 triangle the side opposite the 30° angle is ${short} cm. How long is the hypotenuse?`
          : `In a 30-60-90 triangle the hypotenuse is ${hypotenuse} cm. How long is the side opposite the 30° angle?`,
        unit: 'cm',
        figure: { kind: 'shape', shape: 'triangle', labels: { base: '30°', height: `${short}`, hypotenuse: `${hypotenuse}` } },
        value: wantHypotenuse ? hypotenuse : short,
        summary: `In a 30-60-90 triangle the sides are in the ratio 1 : √3 : 2, so the hypotenuse is exactly twice the side opposite the 30° angle.`,
        hint: 'The shortest side faces the smallest angle, and the hypotenuse is exactly double it.',
        steps: [
          { label: 'The ratio of the sides.', latex: `1 : \\sqrt{3} : 2` },
          {
            label: wantHypotenuse ? 'Double the short side.' : 'Halve the hypotenuse.',
            latex: wantHypotenuse ? `${short} \\times 2 = ${hypotenuse}` : `${hypotenuse} \\div 2 = ${short}`,
          },
        ],
        traps: [
          ...(wantHypotenuse
            ? [trap(roundTo(short / 2, 4), 'special-triangle-ratio-swapped')]
            : [trap(hypotenuse * 2, 'special-triangle-ratio-swapped')]),
        ],
      });
    },
  }),

  template({
    id: 'circle-inscribed-angle',
    conceptId: 'circle-theorems',
    description: 'Relate an inscribed angle to the central angle on the same arc.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const inscribed = rng.int(15, 80);
      const central = inscribed * 2;
      const fromCentral = rng.bool();

      return numericExercise({
        prompt: fromCentral
          ? `An angle at the centre of a circle is ${central}°. What is the angle at the circumference standing on the same arc?`
          : `An angle at the circumference of a circle is ${inscribed}°. What is the angle at the centre standing on the same arc?`,
        unit: 'degrees',
        figure: { kind: 'circle-diagram', elements: ['central-angle', 'inscribed-angle'] },
        value: fromCentral ? inscribed : central,
        summary: `The angle at the centre is twice the angle at the circumference on the same arc, so the answer is ${fromCentral ? inscribed : central}°.`,
        hint: 'One of these two angles is exactly double the other. Which one is at the centre?',
        steps: [
          {
            label: fromCentral ? 'Halve the central angle.' : 'Double the inscribed angle.',
            latex: fromCentral ? `${central} \\div 2 = ${inscribed}` : `${inscribed} \\times 2 = ${central}`,
          },
        ],
        traps: [trap(fromCentral ? central : inscribed, 'inscribed-angle-not-halved')],
      });
    },
  }),

  template({
    id: 'circle-tangent-angle',
    conceptId: 'circle-theorems',
    description: 'Use the right angle between a tangent and a radius.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const given = rng.int(20, 70);
      const answer = 90 - given;

      return numericExercise({
        prompt: `A tangent touches a circle at point T, and a radius is drawn to T. Another line from T makes an angle of ${given}° with the radius. What angle does it make with the tangent?`,
        unit: 'degrees',
        figure: { kind: 'circle-diagram', elements: ['tangent', 'radius'] },
        value: answer,
        summary: `The tangent meets the radius at 90°, so the two angles add to 90: 90 − ${given} = ${answer}°.`,
        hint: 'What angle does a tangent always make with the radius drawn to the point of contact?',
        steps: [
          { label: 'The tangent and radius are perpendicular.', latex: `90` },
          { label: 'Subtract the given angle.', latex: `90 - ${given} = ${answer}` },
        ],
        traps: [trap(180 - given, 'tangent-angle-missed')],
      });
    },
  }),

  template({
    id: 'coordinate-distance-formula',
    conceptId: 'coordinate-geometry',
    description: 'Find the distance between two points.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const triple = rng.pick([
        { dx: 3, dy: 4, d: 5 },
        { dx: 6, dy: 8, d: 10 },
        { dx: 5, dy: 12, d: 13 },
        { dx: 8, dy: 15, d: 17 },
        { dx: 9, dy: 12, d: 15 },
      ]);
      const x1 = rng.int(-6, 2);
      const y1 = rng.int(-6, 2);
      const x2 = x1 + triple.dx;
      const y2 = y1 + triple.dy;

      return numericExercise({
        prompt: `What is the distance between (${x1}, ${y1}) and (${x2}, ${y2})?`,
        figure: {
          kind: 'coordinate-grid',
          min: -10,
          max: 10,
          points: [
            { x: x1, y: y1, label: 'A' },
            { x: x2, y: y2, label: 'B' },
          ],
        },
        value: triple.d,
        summary: `The horizontal gap is ${triple.dx} and the vertical gap is ${triple.dy}, so the distance is √(${triple.dx ** 2} + ${triple.dy ** 2}) = ${triple.d}.`,
        hint: 'The two gaps are the short sides of a right triangle, and the distance is its hypotenuse.',
        steps: [
          { label: 'Find the two gaps.', latex: `\\Delta x = ${triple.dx},\\quad \\Delta y = ${triple.dy}` },
          { label: 'Square and add.', latex: `${triple.dx ** 2} + ${triple.dy ** 2} = ${triple.d ** 2}` },
          { label: 'Take the square root.', latex: `\\sqrt{${triple.d ** 2}} = ${triple.d}` },
        ],
        traps: [
          trap(triple.d ** 2, 'distance-not-rooted'),
          trap(triple.dx + triple.dy, 'root-distributed-over-sum'),
        ],
      });
    },
  }),

  template({
    id: 'coordinate-midpoint',
    conceptId: 'coordinate-geometry',
    description: 'Find a coordinate of the midpoint of a segment.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      // Even gaps keep the midpoint a whole number.
      const x1 = rng.int(-8, 4);
      const y1 = rng.int(-8, 4);
      const x2 = x1 + rng.int(1, 6) * 2;
      const y2 = y1 + rng.int(1, 6) * 2;
      const wantX = rng.bool();
      const answer = wantX ? (x1 + x2) / 2 : (y1 + y2) / 2;
      const differenced = wantX ? (x2 - x1) / 2 : (y2 - y1) / 2;

      return numericExercise({
        prompt: `What is the ${wantX ? 'x' : 'y'}-coordinate of the midpoint of the segment joining (${x1}, ${y1}) and (${x2}, ${y2})?`,
        figure: {
          kind: 'coordinate-grid',
          min: -10,
          max: 10,
          points: [
            { x: x1, y: y1, label: 'A' },
            { x: x2, y: y2, label: 'B' },
            { x: (x1 + x2) / 2, y: (y1 + y2) / 2, label: 'M' },
          ],
        },
        value: answer,
        summary: `A midpoint is an average: (${wantX ? x1 : y1} + ${wantX ? x2 : y2}) ÷ 2 = ${answer}.`,
        hint: 'The midpoint sits halfway between, so average the two coordinates rather than subtracting them.',
        steps: [
          { label: 'Add the two coordinates.', latex: `${wantX ? x1 : y1} + ${wantX ? x2 : y2} = ${wantX ? x1 + x2 : y1 + y2}` },
          { label: 'Halve.', latex: `${answer}` },
        ],
        traps: [...(differenced === answer ? [] : [trap(differenced, 'midpoint-differenced')])],
      });
    },
  }),

  template({
    id: 'special-triangle-45',
    conceptId: 'special-triangles',
    description: 'Use the 45-45-90 ratio.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const leg = rng.int(2, 12);
      const fromHypotenuse = rng.bool();

      return numericExercise({
        prompt: fromHypotenuse
          ? `In a 45-45-90 triangle the hypotenuse is ${leg}√2 cm. How long is each shorter side?`
          : `In a 45-45-90 triangle each shorter side is ${leg} cm. The hypotenuse is a√2 cm — what is a?`,
        unit: 'cm',
        figure: { kind: 'shape', shape: 'triangle', labels: { base: `${leg}`, height: `${leg}`, hypotenuse: '?' } },
        value: leg,
        summary: `In a 45-45-90 triangle the hypotenuse is √2 times a shorter side, so the shorter sides are ${leg} cm.`,
        hint: 'The two shorter sides are equal, and the hypotenuse is √2 times as long.',
        steps: [
          { label: 'The ratio of the sides.', latex: `1 : 1 : \\sqrt{2}` },
          { label: 'Apply it.', latex: `${leg}` },
        ],
        traps: [...(leg * 2 === leg ? [] : [trap(leg * 2, 'special-triangle-ratio-swapped')])],
      });
    },
  }),

  template({
    id: 'trig-choose-ratio',
    conceptId: 'trig-ratios',
    description: 'Choose the trigonometric ratio that uses the sides you have.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const which = rng.pick(['sine', 'cosine', 'tangent'] as const);
      const pair = {
        sine: 'the opposite side and the hypotenuse',
        cosine: 'the adjacent side and the hypotenuse',
        tangent: 'the opposite side and the adjacent side',
      }[which];
      const formula = { sine: '\\sin\\theta = \\frac{O}{H}', cosine: '\\cos\\theta = \\frac{A}{H}', tangent: '\\tan\\theta = \\frac{O}{A}' };

      const { choices, correctId } = options(
        (['sine', 'cosine', 'tangent'] as const).map((name) => ({
          latex: formula[name],
          correct: name === which,
          ...(name === which ? {} : { misconceptionId: 'trig-ratio-confused' }),
        })).concat([{ latex: '\\theta = \\frac{H}{O}', correct: false, misconceptionId: 'hypotenuse-misidentified' }]),
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `You know ${pair} of a right triangle, and want the angle. Which ratio should you use?`,
        choices,
        correctId,
        summary: `SOH-CAH-TOA: ${which} pairs ${pair}.`,
        hint: 'Label the sides relative to the angle first, then find the ratio containing both of the ones you know.',
      });
    },
  }),

  template({
    id: 'trig-find-side',
    conceptId: 'trig-ratios',
    description: 'Find a side of a right triangle using a trigonometric ratio.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const angle = rng.pick([30, 45, 60]);
      const hypotenuse = rng.int(4, 20);
      const radians = (angle * Math.PI) / 180;
      const opposite = roundTo(hypotenuse * Math.sin(radians), 2);
      const adjacent = roundTo(hypotenuse * Math.cos(radians), 2);
      const wantOpposite = rng.bool();
      const answer = wantOpposite ? opposite : adjacent;
      const other = wantOpposite ? adjacent : opposite;

      return numericExercise({
        prompt: `A right triangle has a hypotenuse of ${hypotenuse} cm and an angle of ${angle}°. How long is the side ${wantOpposite ? 'opposite' : 'adjacent to'} that angle? Give your answer to two decimal places.`,
        unit: 'cm',
        figure: {
          kind: 'shape',
          shape: 'triangle',
          labels: {
            hypotenuse: `${hypotenuse} cm`,
            angle: `${angle}°`,
            ...(wantOpposite ? { height: '?' } : { base: '?' }),
          },
        },
        value: answer,
        tolerance: decimalTolerance(answer, 2),
        summary: `${wantOpposite ? 'sin' : 'cos'} ${angle}° = ${wantOpposite ? 'opposite' : 'adjacent'} ÷ hypotenuse, so the side is ${hypotenuse} × ${wantOpposite ? 'sin' : 'cos'} ${angle}° = ${answer} cm.`,
        hint: `You have the hypotenuse and want the ${wantOpposite ? 'opposite' : 'adjacent'} side. Which of SOH, CAH or TOA has both?`,
        steps: [
          {
            label: 'Choose the ratio with the sides you have.',
            latex: wantOpposite ? `\\sin ${angle}^\\circ = \\frac{O}{${hypotenuse}}` : `\\cos ${angle}^\\circ = \\frac{A}{${hypotenuse}}`,
          },
          { label: 'Rearrange and evaluate.', latex: `${hypotenuse} \\times ${wantOpposite ? '\\sin' : '\\cos'} ${angle}^\\circ = ${answer}` },
        ],
        traps: [...(other === answer ? [] : [trap(other, 'trig-ratio-confused')])],
      });
    },
  }),

  /* ------------------------------------------------------ arcs and sectors */
  template({
    id: 'arc-length',
    conceptId: 'arc-sector',
    description: 'Find the arc length of a sector from its angle and radius.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const angle = rng.pick(
        difficulty === 3 ? [30, 45, 60, 90, 120, 180] : [72, 100, 135, 144, 150, 210, 225, 240, 270, 300],
      );
      const radius = rng.int(3, difficulty === 3 ? 12 : 24);
      const answer = roundTo((angle / 360) * 2 * Math.PI * radius, 2);
      const whole = roundTo(2 * Math.PI * radius, 2);
      const sector = roundTo((angle / 360) * Math.PI * radius * radius, 2);

      return numericExercise({
        prompt: `A sector of a circle of radius ${radius} cm has an angle of ${angle}° at the centre. How long is its arc? Give your answer to two decimal places.`,
        unit: 'cm',
        figure: { kind: 'circle-sector', degrees: angle, radiusLabel: `${radius} cm`, arcLabel: '?' },
        value: answer,
        tolerance: decimalTolerance(answer, 2),
        summary: `The sector is ${angle}/360 of the circle, and the whole circumference is 2π × ${radius} = ${whole} cm, so the arc is ${answer} cm.`,
        hint: 'What fraction of a full turn is the angle? The arc is that same fraction of the circumference.',
        steps: [
          { label: 'Find the fraction of the circle.', latex: `\\frac{${angle}}{360}` },
          {
            label: 'Take that fraction of the circumference.',
            latex: `\\frac{${angle}}{360} \\times 2\\pi \\times ${radius} = ${answer}`,
          },
        ],
        traps: distinctTraps(answer, [
          [whole, 'sector-fraction-omitted'],
          [sector, 'arc-and-sector-confused'],
        ]),
      });
    },
  }),

  template({
    id: 'sector-area',
    conceptId: 'arc-sector',
    description: 'Find the area of a sector from its angle and radius.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const angle = rng.pick(
        difficulty === 3 ? [30, 45, 60, 90, 120, 180] : [72, 100, 135, 144, 150, 210, 225, 240, 270, 300],
      );
      const radius = rng.int(3, difficulty === 3 ? 12 : 20);
      const answer = roundTo((angle / 360) * Math.PI * radius * radius, 2);
      const whole = roundTo(Math.PI * radius * radius, 2);
      const arc = roundTo((angle / 360) * 2 * Math.PI * radius, 2);

      return numericExercise({
        prompt: `A sector of a circle of radius ${radius} cm has an angle of ${angle}° at the centre. What is its area? Give your answer to two decimal places.`,
        unit: 'square cm',
        figure: { kind: 'circle-sector', degrees: angle, radiusLabel: `${radius} cm` },
        value: answer,
        tolerance: decimalTolerance(answer, 2),
        summary: `The whole circle has area π × ${radius}² = ${whole} cm², and the sector is ${angle}/360 of it: ${answer} cm².`,
        hint: 'The same fraction as for the arc — but take it of the area, πr², this time.',
        steps: [
          { label: 'Find the area of the whole circle.', latex: `\\pi \\times ${radius}^2 = ${whole}` },
          { label: 'Take the sector’s fraction of it.', latex: `\\frac{${angle}}{360} \\times ${whole} = ${answer}` },
        ],
        traps: distinctTraps(answer, [
          [whole, 'sector-fraction-omitted'],
          [arc, 'arc-and-sector-confused'],
        ]),
      });
    },
  }),

  template({
    id: 'sector-angle-from-arc',
    conceptId: 'arc-sector',
    description: 'Recover the angle of a sector from its arc length.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      /*
       * The arc is quoted as a multiple of π rather than as a decimal, so the
       * angle comes back exactly. Rounding the arc first and asking for the
       * angle it came from yields answers like 119.7°, which teaches a student
       * to distrust arithmetic that was in fact perfectly right.
       */
      const pairs: [number, number][] = [];
      for (const angle of [30, 40, 45, 60, 72, 90, 120, 135, 144, 150, 180, 210, 225, 240, 270, 300]) {
        for (const radius of [3, 4, 5, 6, 8, 9, 10, 12, 15, 18, 20, 24]) {
          if ((angle * radius) % 180 === 0) pairs.push([angle, radius]);
        }
      }
      const [angle, radius] = rng.pick(pairs) as [number, number];
      const coefficient = (angle * radius) / 180;
      // "1π cm" is not how anyone writes it, and generated content that says so reads as generated.
      const arcLength = `${coefficient === 1 ? '' : coefficient}π cm`;
      const inverted = roundTo((2 * radius * 360) / coefficient, 2);

      return numericExercise({
        prompt: `An arc ${arcLength} long is cut from a circle of radius ${radius} cm. What angle does it subtend at the centre?`,
        unit: 'degrees',
        figure: { kind: 'circle-sector', degrees: angle, radiusLabel: `${radius} cm`, arcLabel: arcLength },
        value: angle,
        summary: `The whole circumference is ${2 * radius}π cm, so the arc is ${coefficient}/${2 * radius} of the circle — and that fraction of 360° is ${angle}°.`,
        hint: 'Work out what fraction of the whole circumference the arc is, then take the same fraction of 360°.',
        steps: [
          {
            label: 'Compare the arc with the whole circumference.',
            latex: `\\frac{${coefficient}\\pi}{${2 * radius}\\pi} = \\frac{${coefficient}}{${2 * radius}}`,
          },
          { label: 'Take that fraction of a full turn.', latex: `\\frac{${coefficient}}{${2 * radius}} \\times 360 = ${angle}` },
        ],
        traps: distinctTraps(angle, [
          [inverted, 'sector-fraction-inverted'],
          /*
           * πd read as πr: the circle is taken to be half the size it is, so
           * the fraction doubles. Only offered where the doubled angle is
           * still an angle — nobody types 600° and means it.
           */
          ...(angle * 2 <= 360 ? ([[angle * 2, 'diameter-for-radius']] as [number, string][]) : []),
        ]),
      });
    },
  }),

  template({
    id: 'sector-perimeter',
    conceptId: 'arc-sector',
    description: 'Find the whole perimeter of a sector, arc and radii together.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const angle = rng.pick([30, 45, 60, 72, 90, 120, 135, 144, 150, 180, 225, 240, 270, 300]);
      const radius = rng.int(4, 22);
      const arc = (angle / 360) * 2 * Math.PI * radius;
      const answer = roundTo(arc + 2 * radius, 2);
      const arcOnly = roundTo(arc, 2);
      const wholeCircle = roundTo(2 * Math.PI * radius + 2 * radius, 2);

      return numericExercise({
        prompt: `A sector of radius ${radius} cm has an angle of ${angle}°. What is the perimeter of the whole sector? Give your answer to two decimal places.`,
        unit: 'cm',
        figure: { kind: 'circle-sector', degrees: angle, radiusLabel: `${radius} cm`, label: 'perimeter = arc + two radii' },
        value: answer,
        tolerance: decimalTolerance(answer, 2),
        summary: `The arc is ${arcOnly} cm, and the two straight edges add another 2 × ${radius} = ${2 * radius} cm, giving ${answer} cm.`,
        hint: 'Trace the edge of the slice with a finger. The curve is not the whole journey.',
        steps: [
          { label: 'Find the arc.', latex: `\\frac{${angle}}{360} \\times 2\\pi \\times ${radius} = ${arcOnly}` },
          { label: 'Add the two radii.', latex: `${arcOnly} + 2 \\times ${radius} = ${answer}` },
        ],
        traps: distinctTraps(answer, [
          [arcOnly, 'sector-perimeter-radii-omitted'],
          [wholeCircle, 'sector-fraction-omitted'],
        ]),
      });
    },
  }),

  /* ----------------------------------------------- solving right triangles */
  template({
    id: 'trig-choose-inverse',
    conceptId: 'trig-applications',
    description: 'Choose the calculation that turns two known sides into an angle.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const which = rng.pick(['sin', 'cos', 'tan'] as const);
      const [sides, ratio] = {
        sin: ['the opposite side and the hypotenuse', '\\frac{O}{H}'],
        cos: ['the adjacent side and the hypotenuse', '\\frac{A}{H}'],
        tan: ['the opposite side and the adjacent side', '\\frac{O}{A}'],
      }[which] as [string, string];
      const other = ({ sin: 'cos', cos: 'tan', tan: 'sin' } as const)[which];

      const { choices, correctId } = options(
        [
          { latex: `\\theta = \\${which}^{-1}\\left(${ratio}\\right)`, correct: true },
          { latex: `\\theta = \\${which}\\left(${ratio}\\right)`, misconceptionId: 'inverse-trig-omitted' },
          { latex: `\\theta = \\${other}^{-1}\\left(${ratio}\\right)`, misconceptionId: 'trig-ratio-confused' },
          { latex: `\\theta = \\frac{1}{\\${which}\\left(${ratio}\\right)}`, misconceptionId: 'inverse-as-reciprocal' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `In a right triangle you know ${sides}, and you want the angle θ. Which calculation gives it?`,
        choices,
        correctId,
        summary: `${which} pairs ${sides}, and the inverse runs it backwards: from the ratio to the angle that produced it.`,
        hint: 'First pick the ratio holding the two sides you know. Then ask which way you are travelling — angle to ratio, or ratio to angle?',
      });
    },
  }),

  template({
    id: 'trig-find-angle',
    conceptId: 'trig-applications',
    description: 'Find an angle of a right triangle from two of its sides.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      // Pythagorean triples, so the third side is exact and the picture closes.
      const [a, b, c] = rng.pick([
        [3, 4, 5],
        [5, 12, 13],
        [8, 15, 17],
        [7, 24, 25],
        [20, 21, 29],
        [9, 40, 41],
      ]) as [number, number, number];
      const scale = difficulty === 3 ? 1 : rng.int(1, 3);
      const [opposite, adjacent, hypotenuse] = [a * scale, b * scale, c * scale];
      const which = rng.pick(['sin', 'cos', 'tan'] as const);
      const [given, ratio] = {
        sin: [`a side of ${opposite} cm opposite θ and a hypotenuse of ${hypotenuse} cm`, opposite / hypotenuse],
        cos: [`a side of ${adjacent} cm adjacent to θ and a hypotenuse of ${hypotenuse} cm`, adjacent / hypotenuse],
        tan: [`a side of ${opposite} cm opposite θ and a side of ${adjacent} cm adjacent to it`, opposite / adjacent],
      }[which] as [string, number];
      const answer = roundTo(inverseDegrees(which, ratio), 1);
      const complement = roundTo(90 - answer, 1);
      const shown = roundTo(ratio, 2);

      return numericExercise({
        prompt: `A right triangle has ${given}. Find θ to one decimal place.`,
        unit: 'degrees',
        figure: {
          kind: 'shape',
          shape: 'triangle',
          /*
           * The right angle sits at the bottom left and θ at the bottom right,
           * so the vertical leg is θ's opposite side and the base is its
           * adjacent one. Labelling the given length on the wrong leg would
           * contradict the question in the one place a student checks.
           */
          labels: {
            angle: 'θ',
            ...(which === 'sin' ? { height: `${opposite} cm`, hypotenuse: `${hypotenuse} cm` } : {}),
            ...(which === 'cos' ? { base: `${adjacent} cm`, hypotenuse: `${hypotenuse} cm` } : {}),
            ...(which === 'tan' ? { base: `${adjacent} cm`, height: `${opposite} cm` } : {}),
          },
        },
        value: answer,
        tolerance: decimalTolerance(answer, 1),
        summary: `${which} θ = ${shown}, so θ = ${which}⁻¹(${shown}) = ${answer}°.`,
        hint: 'Form the ratio first. Then use the inverse key — sin⁻¹, cos⁻¹ or tan⁻¹ — to get back from the ratio to the angle.',
        steps: [
          { label: 'Form the ratio.', latex: `\\${which}\\,\\theta = ${shown}` },
          { label: 'Undo it.', latex: `\\theta = \\${which}^{-1}(${shown}) = ${answer}^\\circ` },
        ],
        traps: distinctTraps(answer, [
          [complement, 'trig-ratio-confused'],
          [shown, 'inverse-trig-omitted'],
        ], 0.2),
      });
    },
  }),

  template({
    id: 'trig-depression-angle',
    conceptId: 'trig-applications',
    description: 'Match an angle of depression to the angle of elevation looking back.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const angle = rng.int(10, 70);
      const height = rng.int(12, 95);

      return numericExercise({
        prompt: `From the top of a ${height} m cliff, the angle of depression of a boat is ${angle}°. Looking back from the boat, what is the angle of elevation of the top of the cliff?`,
        unit: 'degrees',
        value: angle,
        summary: `The horizontal at the top of the cliff and the water are parallel, so the line of sight is a transversal and the two angles are alternate: ${angle}°.`,
        hint: 'Draw the horizontal line at the top of the cliff. It is parallel to the water, and the line of sight crosses both.',
        steps: [
          { label: 'The horizontal and the water are parallel.', latex: `\\text{alternate angles}` },
          { label: 'So the two angles are equal.', latex: `${angle}^\\circ` },
        ],
        traps: distinctTraps(angle, [
          // The complement is what measuring from the cliff face instead would give.
          [90 - angle, 'elevation-depression-confused'],
          [180 - angle, 'angle-sum-confused'],
        ]),
      });
    },
  }),

  template({
    id: 'trig-elevation',
    conceptId: 'trig-applications',
    description: 'Find a height from an angle of elevation and a distance.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const angle = rng.int(15, 75);
      const distance = rng.int(8, 60);
      const radians = (angle * Math.PI) / 180;
      const answer = roundTo(distance * Math.tan(radians), 2);
      const withSine = roundTo(distance * Math.sin(radians), 2);
      const divided = roundTo(distance / Math.tan(radians), 2);

      return numericExercise({
        prompt: `Standing ${distance} m from the foot of a tower, the angle of elevation of the top is ${angle}°. How tall is the tower? Give your answer to two decimal places.`,
        unit: 'm',
        figure: {
          kind: 'shape',
          shape: 'triangle',
          labels: { base: `${distance} m`, height: '?', hypotenuse: 'line of sight', angle: `${angle}°` },
        },
        value: answer,
        tolerance: decimalTolerance(answer, 2),
        summary: `The height is opposite the angle and the ${distance} m is adjacent to it, so tan ${angle}° = height ÷ ${distance}, and the height is ${answer} m.`,
        hint: 'You are standing on the ground, so the distance runs *along* the ground — adjacent to the angle. Which ratio pairs adjacent with opposite?',
        steps: [
          { label: 'Choose the ratio holding both sides.', latex: `\\tan ${angle}^\\circ = \\frac{h}{${distance}}` },
          { label: 'The unknown is on top, so multiply.', latex: `h = ${distance} \\times \\tan ${angle}^\\circ = ${answer}` },
        ],
        traps: distinctTraps(answer, [
          [withSine, 'trig-ratio-confused'],
          [divided, 'trig-ratio-applied-inverted'],
        ]),
      });
    },
  }),

  template({
    id: 'trig-depression',
    conceptId: 'trig-applications',
    description: 'Find a distance from an angle of depression and a height.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const angle = rng.int(12, 70);
      const height = rng.int(15, 90);
      const radians = (angle * Math.PI) / 180;
      const answer = roundTo(height / Math.tan(radians), 2);
      const multiplied = roundTo(height * Math.tan(radians), 2);
      const withSine = roundTo(height / Math.sin(radians), 2);

      return numericExercise({
        prompt: `From the top of a cliff ${height} m high, the angle of depression of a boat is ${angle}°. How far is the boat from the foot of the cliff? Give your answer to two decimal places.`,
        unit: 'm',
        figure: {
          kind: 'shape',
          shape: 'triangle',
          labels: { height: `${height} m`, base: '?', hypotenuse: 'line of sight', angle: `${angle}°` },
        },
        value: answer,
        tolerance: decimalTolerance(answer, 2),
        summary: `The angle at the boat equals the angle of depression, so tan ${angle}° = ${height} ÷ d, and d = ${height} ÷ tan ${angle}° = ${answer} m.`,
        hint: 'Depression is measured down from the horizontal, and the same angle reappears at the boat looking up. Which side is opposite it, and which adjacent?',
        steps: [
          { label: 'Bring the angle down to the boat.', latex: `\\tan ${angle}^\\circ = \\frac{${height}}{d}` },
          { label: 'The unknown is underneath, so divide.', latex: `d = \\frac{${height}}{\\tan ${angle}^\\circ} = ${answer}` },
        ],
        traps: distinctTraps(answer, [
          [multiplied, 'trig-ratio-applied-inverted'],
          [withSine, 'trig-ratio-confused'],
        ]),
      });
    },
  }),

  /* ------------------------------------- further circle and coordinate work */
  template({
    id: 'circle-semicircle-angle',
    conceptId: 'circle-theorems',
    description: 'Use the right angle in a semicircle.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const given = rng.int(15, 74);
      const answer = 90 - given;

      return numericExercise({
        prompt: `AB is a diameter of a circle, and C is another point on the circle. Angle ABC is ${given}°. What is angle BAC?`,
        unit: 'degrees',
        figure: { kind: 'circle-diagram', elements: ['diameter', 'inscribed-angle'], labels: { given: `angle ABC = ${given}°` } },
        value: answer,
        summary: `Angle ACB stands on a diameter, so it is 90°. The angles of the triangle total 180°, leaving 180 − 90 − ${given} = ${answer}°.`,
        hint: 'One angle of this triangle you know without being told. Which one, and why?',
        steps: [
          { label: 'The angle in a semicircle is a right angle.', latex: `\\angle ACB = 90^\\circ` },
          { label: 'Use the angle sum of the triangle.', latex: `180 - 90 - ${given} = ${answer}` },
        ],
        traps: distinctTraps(answer, [
          [180 - given, 'semicircle-angle-missed'],
          [given, 'inscribed-angle-not-halved'],
        ]),
      });
    },
  }),

  template({
    id: 'circle-cyclic-quadrilateral',
    conceptId: 'circle-theorems',
    description: 'Use the supplementary opposite angles of a cyclic quadrilateral.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const given = rng.int(40, 140);
      const answer = 180 - given;

      return numericExercise({
        prompt: `All four vertices of a quadrilateral lie on a circle. One of its angles is ${given}°. How big is the angle opposite it?`,
        unit: 'degrees',
        figure: { kind: 'polygon', sides: 4, showCentre: true, label: 'all four vertices on the circle' },
        value: answer,
        summary: `Opposite angles of a cyclic quadrilateral are supplementary, so the opposite angle is 180 − ${given} = ${answer}°.`,
        hint: 'Opposite angles here are not equal. They do something else, and 180 is involved.',
        steps: [
          { label: 'Opposite angles add to 180°.', latex: `x + ${given} = 180` },
          { label: 'Subtract.', latex: `x = 180 - ${given} = ${answer}` },
        ],
        traps: distinctTraps(answer, [
          [given, 'cyclic-quadrilateral-not-supplementary'],
          [360 - given, 'angle-sum-confused'],
        ]),
      });
    },
  }),

  template({
    id: 'similarity-volume-scale',
    conceptId: 'similarity',
    description: 'Scale a volume, where the factor is cubed.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const factor = rng.int(2, 5);
      const volume = rng.int(2, 25) * 4;
      const answer = volume * factor ** 3;

      return numericExercise({
        prompt: `Two solids are similar, with a scale factor of ${factor} from the smaller to the larger. The smaller has a volume of ${volume} cm³. What is the volume of the larger?`,
        unit: 'cubic cm',
        value: answer,
        summary: `Every length is multiplied by ${factor}, and a volume is three lengths multiplied together, so it grows by ${factor}³ = ${factor ** 3}: ${answer} cm³.`,
        hint: 'How many directions at once does an enlargement stretch a solid in?',
        steps: [
          { label: 'Volume scales by the cube of the length factor.', latex: `${factor}^3 = ${factor ** 3}` },
          { label: 'Multiply.', latex: `${volume} \\times ${factor ** 3} = ${answer}` },
        ],
        traps: distinctTraps(answer, [
          [volume * factor * factor, 'volume-scaled-as-area'],
          [volume * factor, 'area-scaled-linearly'],
        ]),
      });
    },
  }),

  template({
    id: 'coordinate-circle-equation',
    conceptId: 'coordinate-geometry',
    description: 'Read the centre and radius off the equation of a circle.',
    difficulties: [4, 5],
    build: ({ rng, difficulty }) => {
      const a = rng.int(-8, 8);
      const b = rng.int(-8, 8);
      const radius = rng.int(2, difficulty === 4 ? 9 : 15);
      const bracket = (variable: string, shift: number): string =>
        shift === 0 ? `${variable}^2` : `(${variable} ${shift > 0 ? '-' : '+'} ${Math.abs(shift)})^2`;
      const wantRadius = rng.bool();

      return numericExercise({
        prompt: wantRadius
          ? 'What is the radius of the circle with this equation?'
          : 'What is the x-coordinate of the centre of the circle with this equation?',
        latex: `${bracket('x', a)} + ${bracket('y', b)} = ${radius * radius}`,
        value: wantRadius ? radius : a,
        summary: wantRadius
          ? `The right-hand side is r², so r = √${radius * radius} = ${radius}.`
          : `In (x − a)² + (y − b)² = r² the centre is (a, b), so the x-coordinate of the centre is ${a}.`,
        hint: wantRadius
          ? 'The standard form ends in r², not in r.'
          : 'The bracket subtracts. What number would have to be subtracted to produce what you see?',
        steps: wantRadius
          ? [{ label: 'Take the square root of the right-hand side.', latex: `r = \\sqrt{${radius * radius}} = ${radius}` }]
          : [{ label: 'Match against the standard form.', latex: `${bracket('x', a)} \\Rightarrow a = ${a}` }],
        traps: wantRadius
          ? distinctTraps(radius, [[radius * radius, 'circle-equation-radius-not-rooted']])
          : distinctTraps(a, [[-a, 'circle-equation-centre-sign-flipped']]),
      });
    },
  }),

  template({
    id: 'coordinate-perpendicular-slope',
    conceptId: 'coordinate-geometry',
    description: 'Find the slope of a line perpendicular to a given one.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const pairs: [number, number][] = [];
      for (let p = 2; p <= 9; p += 1) {
        for (let q = 1; q <= 9; q += 1) {
          if (gcd(p, q) === 1) pairs.push([p, q]);
        }
      }
      const [p, q] = rng.pick(pairs) as [number, number];
      const sign = rng.bool() ? 1 : -1;
      const slope = fraction(sign * q, p);
      const answer = fraction(-sign * p, q);

      return fractionExercise({
        prompt: 'A line has the slope shown. What is the slope of any line perpendicular to it?',
        latex: `m = ${toLatex(slope)}`,
        value: answer,
        requireSimplified: true,
        summary: `Perpendicular slopes are negative reciprocals: flip ${toText(slope)} and change its sign, giving ${toText(answer)}. As a check, ${toText(slope)} × ${toText(answer)} = −1.`,
        hint: 'You need a number whose product with this one is −1. What has to happen to a fraction to get there?',
        steps: [
          { label: 'Flip the fraction.', latex: toLatex(fraction(sign * p, q)) },
          { label: 'Change the sign.', latex: toLatex(answer) },
          { label: 'Check the product.', latex: `${toLatex(slope)} \\times ${toLatex(answer)} = -1` },
        ],
        traps: [
          fractionTrap(fraction(sign * p, q), 'perpendicular-slope-not-negated'),
          fractionTrap(fraction(-sign * q, p), 'perpendicular-slope-not-inverted'),
        ],
      });
    },
  }),
];

/** The angle, in degrees, whose named ratio is `value` — sin⁻¹, cos⁻¹ or tan⁻¹. */
function inverseDegrees(which: 'sin' | 'cos' | 'tan', value: number): number {
  const radians = which === 'sin' ? Math.asin(value) : which === 'cos' ? Math.acos(value) : Math.atan(value);
  return (radians * 180) / Math.PI;
}

/**
 * Traps whose values are all distinct — from the answer, and from each other.
 *
 * Two different wrong turns can land on the same number for particular
 * parameters: a 180° sector of radius 4 has an arc and an area that agree to
 * the last decimal. The grader reports the *first* trap matching a value, so
 * an overlap makes it name the wrong misconception — worse than naming none,
 * because it sends the student to repair something they understand. The later
 * claim on a value is therefore dropped.
 */
function distinctTraps(answer: number, entries: [number, string][], slack = 0.02): Trap[] {
  const taken = [answer];
  const kept: Trap[] = [];
  for (const [value, misconceptionId] of entries) {
    if (taken.some((other) => Math.abs(other - value) <= slack)) continue;
    taken.push(value);
    kept.push(trap(value, misconceptionId));
  }
  return kept;
}
